import React, { useEffect, useState } from 'react';
import { useToast } from '@/hooks/use-toast';
import { formatDate } from '@/lib/dateUtils';

interface InterestTransaction {
  id: string;
  transaction_date: string;
  transaction_type: string;
  party_name: string;
  debit_to: string;
  loan_amount: number;
  balance_amount: number;
  interest_paid: number;
  principal_paid: number;
  balance_after_payment: number;
  notes: string;
  created_at: string;
}

interface BusinessParty {
  id: string;
  party_name: string;
  party_type: string;
  opening_balance: number;
}

export default function InterestTransactionsSimple() {
  const { toast } = useToast();
  const [rows, setRows] = useState<InterestTransaction[]>([]);
  const [loading, setLoading] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [searchText, setSearchText] = useState('');
  const [filterForm, setFilterForm] = useState({ from: '', to: '', creditor: '' });
  const [showCount, setShowCount] = useState('All');
  const [businessParties, setBusinessParties] = useState<BusinessParty[]>([]);
  const [loadingParties, setLoadingParties] = useState(false);

  const emptyForm = {
    transaction_date: new Date().toISOString().slice(0, 10),
    party_name: '',
    debit_to: '',
    notes: '',
  };
  const [form, setForm] = useState({ ...emptyForm });

  useEffect(() => {
    fetchList();
    fetchBusinessParties();
  }, []);

  const fetchBusinessParties = async () => {
    try {
      setLoadingParties(true);
      const response = await fetch('/api/business-parties', {
        credentials: 'include'
      });
      const result = await response.json();

      if (result.ok) {
        setBusinessParties(result.rows || []);
      }
    } catch (err) {
      console.error('Failed to load business parties:', err);
    } finally {
      setLoadingParties(false);
    }
  };

  const fetchList = async () => {
    try {
      setLoading(true);
      const res = await fetch("/api/interest-transactions");
      const data = await res.json();
      if (data.ok) {
        const mapped = data.rows.map((r: any) => ({
          ...r,
          id: r._id || r.id,
        }));
        setRows(mapped);
      }
    } catch (err) {
      console.error("Fetch error:", err);
      toast({ title: "Error", description: "Failed to load data", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const validate = () => {
    if (!form.transaction_date) { toast({ title: 'Error', description: 'Select a date', variant: 'destructive' }); return false; }
    if (!form.party_name.trim()) { toast({ title: 'Error', description: 'Enter Credit (From) name', variant: 'destructive' }); return false; }
    if (!form.debit_to.trim()) { toast({ title: 'Error', description: 'Enter Debit (To) name', variant: 'destructive' }); return false; }
    return true;
  };

  const handleSave = async () => {
    if (!validate()) return;

    try {
      setLoading(true);
      const url = editingId
        ? `/api/interest-transactions/${editingId}`
        : "/api/interest-transactions";
      const method = editingId ? "PUT" : "POST";

      const res = await fetch(url, {
        method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });

      const data = await res.json();
      if (data.ok) {
        toast({
          title: "Success",
          description: editingId ? "Updated successfully" : "Saved successfully",
        });
        fetchList();
        resetForm();
      }
    } catch (err) {
      console.error("Save error:", err);
      toast({ title: "Error", description: "Save failed", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setForm({ ...emptyForm });
    setEditingId(null);
  };

  const editRow = (r: InterestTransaction) => {
    setEditingId(r.id);
    setForm({
      transaction_date: r.transaction_date,
      party_name: r.party_name,
      debit_to: r.debit_to || '',
      notes: r.notes || '',
    });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const delRow = async (id: string) => {
    if (!confirm("Delete this transaction?")) return;
    try {
      const res = await fetch(`/api/interest-transactions/${id}`, { method: "DELETE" });
      const data = await res.json();
      if (data.ok) {
        toast({ title: "Deleted", description: "Transaction removed" });
        fetchList();
      }
    } catch (err) {
      console.error("Delete error:", err);
      toast({ title: "Error", description: "Delete failed", variant: "destructive" });
    }
  };

  const filteredRows = rows.filter(r =>
    r.party_name?.toLowerCase().includes(searchText.toLowerCase()) ||
    r.transaction_type?.toLowerCase().includes(searchText.toLowerCase()) ||
    r.notes?.toLowerCase().includes(searchText.toLowerCase())
  ).slice(0, showCount === 'All' ? undefined : parseInt(showCount));

  const fmt = (n: number) => '₹' + (n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  return (
    <div style={{ minHeight: '100vh', background: '#F3F4F6', padding: 24, fontFamily: 'system-ui, sans-serif' }}>
      {/* Header */}
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ fontSize: 28, fontWeight: 700, color: '#111827', margin: 0 }}>Interest Transactions</h1>
        <p style={{ fontSize: 14, color: '#6B7280', margin: '4px 0 0 0' }}>Manage loan and interest records</p>
      </div>

      {/* Blue Form Panel */}
      <div style={{ background: 'linear-gradient(to right, #2563EB, #1D4ED8)', borderRadius: 12, padding: 24, marginBottom: 24, color: '#fff' }}>
        <h2 style={{ fontSize: 16, fontWeight: 600, color: '#fff', marginBottom: 16 }}>
          {editingId ? 'Edit Transaction' : 'New Transaction'}
        </h2>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16, marginBottom: 16 }}>
          <div>
            <label style={{ fontSize: 12, fontWeight: 600, color: '#fff', marginBottom: 4, display: 'block' }}>Date *</label>
            <input
              type="date"
              value={form.transaction_date}
              onChange={e => setForm(p => ({ ...p, transaction_date: e.target.value }))}
              style={{ width: '100%', padding: 10, border: '1px solid rgba(255,255,255,0.5)', borderRadius: 8, fontSize: 14, background: '#fff', color: '#000' }}
            />
          </div>

          <div>
            <label style={{ fontSize: 12, fontWeight: 600, color: '#fff', marginBottom: 4, display: 'block' }}>Credit (From) *</label>
            <select
              value={form.party_name}
              onChange={e => setForm(p => ({ ...p, party_name: e.target.value }))}
              style={{ width: '100%', padding: 10, border: '1px solid rgba(255,255,255,0.5)', borderRadius: 8, fontSize: 14, background: '#fff', color: '#000', cursor: 'pointer' }}
            >
              <option value="">Choose</option>
              {businessParties.map(party => (
                <option key={party.id} value={party.party_name}>
                  {party.party_name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label style={{ fontSize: 12, fontWeight: 600, color: '#fff', marginBottom: 4, display: 'block' }}>Debit (To) *</label>
            <select
              value={form.debit_to}
              onChange={e => setForm(p => ({ ...p, debit_to: e.target.value }))}
              style={{ width: '100%', padding: 10, border: '1px solid rgba(255,255,255,0.5)', borderRadius: 8, fontSize: 14, background: '#fff', color: '#000', cursor: 'pointer' }}
            >
              <option value="">Choose</option>
              {businessParties.map(party => (
                <option key={party.id} value={party.party_name}>
                  {party.party_name}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div style={{ marginBottom: 16 }}>
          <label style={{ fontSize: 12, fontWeight: 600, color: '#fff', marginBottom: 4, display: 'block' }}>Description</label>
          <input
            type="text"
            value={form.notes}
            onChange={e => setForm(p => ({ ...p, notes: e.target.value }))}
            placeholder="Enter description (optional)"
            style={{ width: '100%', padding: 10, border: '1px solid rgba(255,255,255,0.5)', borderRadius: 8, fontSize: 14, background: '#fff', color: '#000' }}
          />
        </div>

        <div style={{ display: 'flex', gap: 12 }}>
          <button
            onClick={handleSave}
            disabled={loading}
            style={{
              background: loading ? 'rgba(255,255,255,0.5)' : '#fff',
              color: '#2563EB',
              border: 'none',
              borderRadius: 8,
              padding: '10px 24px',
              fontSize: 14,
              fontWeight: 600,
              cursor: loading ? 'not-allowed' : 'pointer',
            }}
          >
            {loading ? 'Saving...' : editingId ? 'Update' : 'Save'}
          </button>
          {editingId && (
            <button
              onClick={resetForm}
              style={{
                background: 'rgba(255,255,255,0.2)',
                color: '#fff',
                border: '1px solid rgba(255,255,255,0.5)',
                borderRadius: 8,
                padding: '10px 24px',
                fontSize: 14,
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              Cancel
            </button>
          )}
        </div>
      </div>

      {/* Filter Section */}
      <div style={{ background: '#fff', borderRadius: 12, padding: 24, marginBottom: 24, boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
        <h3 style={{ fontSize: 16, fontWeight: 600, color: '#111827', marginBottom: 16 }}>Filter Date</h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16, marginBottom: 16 }}>
          <div>
            <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 4, display: 'block' }}>Search From</label>
            <input
              type="date"
              value={filterForm.from}
              onChange={e => setFilterForm(p => ({ ...p, from: e.target.value }))}
              style={{ width: '100%', padding: 10, border: '1px solid #D1D5DB', borderRadius: 8, fontSize: 14 }}
            />
          </div>
          <div>
            <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 4, display: 'block' }}>To</label>
            <input
              type="date"
              value={filterForm.to}
              onChange={e => setFilterForm(p => ({ ...p, to: e.target.value }))}
              style={{ width: '100%', padding: 10, border: '1px solid #D1D5DB', borderRadius: 8, fontSize: 14 }}
            />
          </div>
          <div>
            <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 4, display: 'block' }}>Creditor</label>
            <select
              value={filterForm.creditor}
              onChange={e => setFilterForm(p => ({ ...p, creditor: e.target.value }))}
              style={{ width: '100%', padding: 10, border: '1px solid #D1D5DB', borderRadius: 8, fontSize: 14, background: '#fff', cursor: 'pointer' }}
            >
              <option value="">Choose</option>
              {businessParties.map(party => (
                <option key={party.id} value={party.party_name}>
                  {party.party_name}
                </option>
              ))}
            </select>
          </div>
        </div>
        <button
          onClick={() => {
            const filtered = rows.filter(r => {
              const matchFrom = !filterForm.from || r.transaction_date >= filterForm.from;
              const matchTo = !filterForm.to || r.transaction_date <= filterForm.to;
              const matchCreditor = !filterForm.creditor || r.party_name?.toLowerCase().includes(filterForm.creditor.toLowerCase());
              return matchFrom && matchTo && matchCreditor;
            });
            setSearchText('');
          }}
          style={{
            background: '#2563EB',
            color: '#fff',
            border: 'none',
            borderRadius: 8,
            padding: '10px 24px',
            fontSize: 14,
            fontWeight: 600,
            cursor: 'pointer',
          }}
        >
          Search
        </button>
      </div>

      {/* Search */}
      <div style={{ marginBottom: 16, display: 'flex', gap: 16, alignItems: 'center' }}>
        <input
          type="text"
          value={searchText}
          onChange={e => setSearchText(e.target.value)}
          placeholder="Type to filter..."
          style={{ flex: 1, maxWidth: 400, padding: 10, border: '1px solid #D1D5DB', borderRadius: 8, fontSize: 14 }}
        />
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 14, color: '#374151', fontWeight: 500 }}>Show:</span>
          <select
            value={showCount}
            onChange={e => setShowCount(e.target.value)}
            style={{ padding: 8, border: '1px solid #D1D5DB', borderRadius: 8, fontSize: 14, background: '#fff', cursor: 'pointer' }}
          >
            <option value="All">All</option>
            <option value="10">10</option>
            <option value="25">25</option>
            <option value="50">50</option>
          </select>
        </div>
      </div>

      {/* Table */}
      <div style={{ background: '#fff', borderRadius: 12, boxShadow: '0 1px 3px rgba(0,0,0,0.1)', overflow: 'hidden' }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: '#F9FAFB', borderBottom: '1px solid #E5E7EB' }}>
                <th style={{ padding: 12, textAlign: 'left', fontSize: 12, fontWeight: 600, color: '#374151' }}>S.No</th>
                <th style={{ padding: 12, textAlign: 'left', fontSize: 12, fontWeight: 600, color: '#374151' }}>Date</th>
                <th style={{ padding: 12, textAlign: 'left', fontSize: 12, fontWeight: 600, color: '#374151' }}>Trans Type</th>
                <th style={{ padding: 12, textAlign: 'left', fontSize: 12, fontWeight: 600, color: '#374151' }}>Creditor</th>
                <th style={{ padding: 12, textAlign: 'right', fontSize: 12, fontWeight: 600, color: '#374151' }}>Loan Amount</th>
                <th style={{ padding: 12, textAlign: 'right', fontSize: 12, fontWeight: 600, color: '#374151' }}>Balance Amount</th>
                <th style={{ padding: 12, textAlign: 'right', fontSize: 12, fontWeight: 600, color: '#374151' }}>Interest Paid</th>
                <th style={{ padding: 12, textAlign: 'right', fontSize: 12, fontWeight: 600, color: '#374151' }}>Principal Paid</th>
                <th style={{ padding: 12, textAlign: 'right', fontSize: 12, fontWeight: 600, color: '#374151' }}>Balance after payment</th>
                <th style={{ padding: 12, textAlign: 'left', fontSize: 12, fontWeight: 600, color: '#374151' }}>Description</th>
                <th style={{ padding: 12, textAlign: 'center', fontSize: 12, fontWeight: 600, color: '#374151' }}>Action</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={11} style={{ padding: 24, textAlign: 'center', color: '#6B7280' }}>
                    Loading...
                  </td>
                </tr>
              ) : filteredRows.length === 0 ? (
                <tr>
                  <td colSpan={11} style={{ padding: 24, textAlign: 'center', color: '#6B7280' }}>
                    No data available in table
                  </td>
                </tr>
              ) : (
                filteredRows.map((r, i) => (
                  <tr key={r.id} style={{ borderBottom: '1px solid #E5E7EB' }}>
                    <td style={{ padding: 12, fontSize: 14, color: '#111827' }}>
                      {i + 1}
                    </td>
                    <td style={{ padding: 12, fontSize: 14, color: '#111827' }}>
                      {formatDate(r.transaction_date)}
                    </td>
                    <td style={{ padding: 12, fontSize: 14, color: '#111827' }}>
                      <span style={{
                        padding: '4px 8px',
                        borderRadius: 4,
                        fontSize: 12,
                        fontWeight: 500,
                        background: r.transaction_type === 'Loan Given' ? '#D1FAE5' :
                                   r.transaction_type === 'Loan Taken' ? '#FEE2E2' :
                                   r.transaction_type === 'Interest Received' ? '#DBEAFE' :
                                   r.transaction_type === 'Interest Paid' ? '#FEF3C7' : '#F3F4F6',
                        color: r.transaction_type === 'Loan Given' ? '#065F46' :
                               r.transaction_type === 'Loan Taken' ? '#991B1B' :
                               r.transaction_type === 'Interest Received' ? '#1E40AF' :
                               r.transaction_type === 'Interest Paid' ? '#92400E' : '#374151',
                      }}>
                        {r.transaction_type || 'N/A'}
                      </span>
                    </td>
                    <td style={{ padding: 12, fontSize: 14, fontWeight: 500, color: '#111827' }}>
                      {r.party_name}
                    </td>
                    <td style={{ padding: 12, fontSize: 14, textAlign: 'right', color: '#111827' }}>
                      {fmt(r.loan_amount || 0)}
                    </td>
                    <td style={{ padding: 12, fontSize: 14, textAlign: 'right', color: '#111827' }}>
                      {fmt(r.balance_amount || 0)}
                    </td>
                    <td style={{ padding: 12, fontSize: 14, textAlign: 'right', color: '#111827' }}>
                      {fmt(r.interest_paid || 0)}
                    </td>
                    <td style={{ padding: 12, fontSize: 14, textAlign: 'right', color: '#111827' }}>
                      {fmt(r.principal_paid || 0)}
                    </td>
                    <td style={{ padding: 12, fontSize: 14, textAlign: 'right', color: '#111827' }}>
                      {fmt(r.balance_after_payment || 0)}
                    </td>
                    <td style={{ padding: 12, fontSize: 14, color: '#6B7280', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {r.notes || '-'}
                    </td>
                    <td style={{ padding: 12, textAlign: 'center' }}>
                      <button
                        onClick={() => editRow(r)}
                        style={{
                          background: '#EFF6FF',
                          color: '#2563EB',
                          border: 'none',
                          borderRadius: 4,
                          padding: '6px 12px',
                          fontSize: 12,
                          fontWeight: 500,
                          cursor: 'pointer',
                          marginRight: 4,
                        }}
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => delRow(r.id)}
                        style={{
                          background: '#FEF2F2',
                          color: '#DC2626',
                          border: 'none',
                          borderRadius: 4,
                          padding: '6px 12px',
                          fontSize: 12,
                          fontWeight: 500,
                          cursor: 'pointer',
                        }}
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
